import { Decimal } from 'decimal.js'

function positive(value: string | undefined): Decimal | null {
  if (!value) return null
  try {
    const d = new Decimal(value.replaceAll(',', ''))
    return d.isFinite() && d.gt(0) ? d : null
  } catch {
    return null
  }
}

/**
 * Is an opening order's size under the venue's minimum notional (quote currency)?
 * A no-op when the minimum is absent, the order reduces, the size is unparseable, or a
 * base size has no reference price (the venue decides then).
 */
export function belowMinNotional(o: {
  minNotional?: string
  reduceOnly: boolean
  size: string
  denomination: 'base' | 'quote'
  refPrice?: string
}): { below: boolean; min?: string } {
  if (o.reduceOnly) return { below: false }
  const min = positive(o.minNotional)
  const size = positive(o.size)
  if (!min || !size) return { below: false }
  let notional = size
  if (o.denomination === 'base') {
    const price = positive(o.refPrice)
    if (!price) return { below: false }
    notional = size.mul(price)
  }
  return notional.lt(min) ? { below: true, min: min.toFixed() } : { below: false }
}

/**
 * Row-level wrapper: derives the denomination from the row's own unit, and exempts options
 * (no per-order notional floor), rows without a catalogue instrument, locked full-position
 * closes (reduce-only in effect) and rows whose unit is not yet known.
 */
export function rowBelowMinNotional(o: {
  item: {
    itemType: 'instrument' | 'option'
    amount?: string
    amountUnit?: string
    quoteCurrency?: string
    lockedAmount?: boolean
  }
  instrument?: { minNotional?: string }
  refPrice?: string
  reduceOnly: boolean
}): { below: boolean; min?: string } {
  const { item, instrument } = o
  if (!instrument || item.itemType === 'option' || item.lockedAmount) return { below: false }
  if (!item.amountUnit) return { below: false }
  return belowMinNotional({
    minNotional: instrument.minNotional,
    reduceOnly: o.reduceOnly,
    size: item.amount ?? '',
    denomination: item.amountUnit === item.quoteCurrency ? 'quote' : 'base',
    refPrice: o.refPrice,
  })
}

/** The one reference-price chain: watchlist ticker, then the streamed price, then the row's own. */
export function resolveRefPrice(o: {
  watchlist?: string
  streamed?: string
  itemPrice?: string
}): string | undefined {
  return o.watchlist || o.streamed || o.itemPrice || undefined
}

/** The unit a row's amount is really in: its own unit when it names a ticker, else the default. */
export function effectiveAmountUnit(
  rawUnit: string | undefined,
  labels: string[],
  defaultUnit: string
): string {
  return rawUnit && labels.includes(rawUnit) ? rawUnit : defaultUnit
}

export interface MinNotionalRowInputs {
  item: {
    itemType: 'instrument' | 'option'
    amount?: string
    lockedAmount?: boolean
  }
  instrument?: { minNotional?: string }
  quoteCurrency: string
  /** Already resolved with `effectiveAmountUnit`. */
  amountUnit: string
  refPrice?: string
}

export type MinNotionalByIndex = Record<number, { below: boolean; min?: string }>

/** Per-row results, computed once; the row message and the submit block both read this. */
export function computeMinNotionalByIndex(
  rows: MinNotionalRowInputs[],
  reduceOnly: boolean
): MinNotionalByIndex {
  const out: MinNotionalByIndex = {}
  rows.forEach((row, index) => {
    out[index] = rowBelowMinNotional({
      item: { ...row.item, amountUnit: row.amountUnit, quoteCurrency: row.quoteCurrency },
      instrument: row.instrument,
      refPrice: row.refPrice,
      reduceOnly,
    })
  })
  return out
}

export function anyBelowMinNotional(byIndex: MinNotionalByIndex | undefined): boolean {
  return Object.values(byIndex ?? {}).some((r) => r.below)
}
